import { useEffect, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  Clock3,
  ImagePlus,
  LockKeyhole,
  Send,
  ShieldAlert,
  Sparkles,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { queueBandMeta } from "../format";
import {
  photoKindLabel,
  REPORT_PHOTO_ACCEPT,
  validateReportPhoto,
} from "../reportPhoto";
import type {
  EvaluatedToilet,
  ObservedStatus,
  PendingReport,
  QueueBand,
  ReportPhotoDraft,
  ReportPhotoUploadStatus,
  ToiletPhotoKind,
} from "../types";
import { useModalDialog } from "../useModalDialog";

const reportChoices: {
  status: ObservedStatus;
  label: string;
  description: string;
  icon: typeof Check;
}[] = [
  { status: "available", label: "使えた", description: "問題なく利用できた", icon: Check },
  {
    status: "unavailable",
    label: "使えなかった",
    description: "閉鎖・故障などで利用できない",
    icon: ShieldAlert,
  },
  { status: "queue", label: "行列がある", description: "待ち時間が発生している", icon: Users },
  {
    status: "unsanitary",
    label: "衛生上使いにくい",
    description: "汚れ・臭いなどが気になる",
    icon: Sparkles,
  },
];

export function ReportDialog({
  item,
  onClose,
  onSubmit,
  onViewDetail,
  enablePhotoUpload = true,
  simulated = false,
}: {
  item: EvaluatedToilet;
  onClose: () => void;
  onSubmit: (
    input: Omit<PendingReport, "id" | "deviceId" | "reportedAt">,
    photo?: ReportPhotoDraft,
  ) => Promise<{
    queued: boolean;
    simulated?: boolean;
    photoStatus?: ReportPhotoUploadStatus;
    photoId?: string;
  }>;
  onViewDetail?: () => void;
  enablePhotoUpload?: boolean;
  simulated?: boolean;
}) {
  const [status, setStatus] = useState<ObservedStatus>();
  const [queueBand, setQueueBand] = useState<QueueBand>();
  const [photo, setPhoto] = useState<ReportPhotoDraft>();
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>();
  const [photoError, setPhotoError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    kind: "sent" | "queued" | "simulated";
    includedPhoto: boolean;
    photoStatus?: ReportPhotoUploadStatus;
  }>();
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photoPreviewUrlRef = useRef<string | undefined>(undefined);
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const closeIfIdle = () => {
    if (!submitting) onClose();
  };
  const dialogRef = useModalDialog<HTMLElement>(closeIfIdle);

  useEffect(
    () => () => {
      if (photoPreviewUrlRef.current) URL.revokeObjectURL(photoPreviewUrlRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!reviewing || result) return;
    reviewHeadingRef.current?.focus();
  }, [reviewing, result]);

  const replacePhoto = (file: File) => {
    const validationError = validateReportPhoto(file);
    if (validationError) {
      setPhotoError(validationError);
      return;
    }
    if (photoPreviewUrlRef.current) URL.revokeObjectURL(photoPreviewUrlRef.current);
    const previewUrl = URL.createObjectURL(file);
    photoPreviewUrlRef.current = previewUrl;
    setPhotoPreviewUrl(previewUrl);
    setPhoto((current) => ({ file, kind: current?.kind ?? "exterior" }));
    setPhotoError(undefined);
    setError(undefined);
  };

  const removePhoto = () => {
    if (photoPreviewUrlRef.current) URL.revokeObjectURL(photoPreviewUrlRef.current);
    photoPreviewUrlRef.current = undefined;
    setPhotoPreviewUrl(undefined);
    setPhoto(undefined);
    setPhotoError(undefined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!status || (status === "queue" && !queueBand)) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await onSubmit(
        {
          toiletId: item.id,
          status,
          queueBand: status === "queue" ? queueBand : undefined,
        },
        photo,
      );
      setResult({
        kind: response.simulated ? "simulated" : response.queued ? "queued" : "sent",
        includedPhoto: Boolean(photo),
        photoStatus: response.photoStatus,
      });
    } catch {
      setError("送信できませんでした。接続を確認して、もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedChoice = reportChoices.find((choice) => choice.status === status);
  const resultMessage = !result
    ? ""
    : result.kind === "queued"
      ? result.includedPhoto
        ? "状況報告はオフライン送信待ちに保存しました。写真は端末へ保存しないため、接続後にもう一度選んでください。"
        : "オフライン送信待ちに保存しました。接続が戻ると自動送信します。"
      : result.kind === "simulated"
        ? result.photoStatus === "local"
          ? "訓練シナリオだけに反映しました。写真はこの端末の詳細画面だけに表示し、送信・保存していません。"
          : "訓練シナリオだけに反映しました。実際の現場報告としては送信していません。"
        : result.photoStatus === "uploaded"
          ? "状況を反映し、写真を掲載確認へ送りました。確認が終わるまではこの端末だけに表示します。"
          : result.photoStatus === "not_uploaded"
            ? "状況は反映しましたが、写真は送信できませんでした。写真はこの端末だけに表示します。接続を確認して、必要ならもう一度投稿してください。"
            : result.photoStatus === "local"
              ? "状況を反映しました。写真は実証表示として、この端末の詳細画面だけに表示します。"
              : "現在の候補判定へ反映しました。1件だけで閉鎖とは判定しません。";

  return (
    <div className="dialog-backdrop" onMouseDown={closeIfIdle}>
      <section
        ref={dialogRef}
        className="report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <p className="dialog-step" aria-live="polite">
              {result
                ? "報告を受け付けました"
                : reviewing
                  ? "送信内容の確認"
                  : simulated
                    ? "実証モード・外部へ送信しません"
                    : "1分以内で完了"}
            </p>
            <h2 id="report-title">{item.name}</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={closeIfIdle}
            disabled={submitting}
            data-dialog-initial-focus
          >
            <X aria-hidden="true" />
            <span className="sr-only">報告を閉じる</span>
          </button>
        </div>

        {result ? (
          <div className="report-success" role="status">
            <span>
              <Check aria-hidden="true" />
            </span>
            <h3>ご報告ありがとうございます</h3>
            <p>{resultMessage}</p>
            <button
              className="primary-button"
              type="button"
              onClick={result.includedPhoto && onViewDetail ? onViewDetail : onClose}
            >
              {result.includedPhoto && onViewDetail
                ? result.photoStatus === "not_uploaded"
                  ? "未送信の写真を確認"
                  : "詳細で写真を見る"
                : "候補へ戻る"}
            </button>
          </div>
        ) : reviewing && selectedChoice ? (
          <div className="report-review">
            <h3 ref={reviewHeadingRef} tabIndex={-1}>
              この内容で報告しますか？
            </h3>
            <dl>
              <div>
                <dt>施設</dt>
                <dd>{item.name}</dd>
              </div>
              <div>
                <dt>状況</dt>
                <dd>{selectedChoice.label}</dd>
              </div>
              {status === "queue" && queueBand && (
                <div>
                  <dt>待ち時間</dt>
                  <dd>{queueBandMeta[queueBand]}</dd>
                </div>
              )}
              {photo && (
                <div>
                  <dt>写真</dt>
                  <dd>{photoKindLabel[photo.kind]}・1枚</dd>
                </div>
              )}
            </dl>
            {photo && photoPreviewUrl && (
              <div className="report-photo-review">
                <b>投稿予定の写真</b>
                <img
                  src={photoPreviewUrl}
                  alt={`投稿予定の${photoKindLabel[photo.kind]}写真`}
                />
                <small>丸い範囲を一覧のサムネイルとして表示します。</small>
              </div>
            )}
            <p>送信後も、1件だけで施設の閉鎖とは判定しません。</p>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <div className="report-review-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={submitting}
                onClick={() => {
                  setReviewing(false);
                  setError(undefined);
                }}
              >
                戻って修正
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={submitting}
                onClick={submit}
              >
                <Send aria-hidden="true" />
                {submitting ? "送信中…" : "この内容を送信"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <fieldset className="report-options">
              <legend>このトイレの状況は？</legend>
              {reportChoices.map((choice) => {
                const Icon = choice.icon;
                return (
                  <button
                    className={status === choice.status ? "is-selected" : ""}
                    type="button"
                    key={choice.status}
                    onClick={() => {
                      setStatus(choice.status);
                      if (choice.status !== "queue") setQueueBand(undefined);
                      setError(undefined);
                    }}
                    aria-pressed={status === choice.status}
                  >
                    <Icon aria-hidden="true" />
                    <span>
                      <b>{choice.label}</b>
                      <small>{choice.description}</small>
                    </span>
                    {status === choice.status && <Check aria-hidden="true" />}
                  </button>
                );
              })}
            </fieldset>

            {status === "queue" && (
              <fieldset className="queue-options">
                <legend>待ち時間を選んでください</legend>
                {(Object.entries(queueBandMeta) as [QueueBand, string][]).map(
                  ([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={queueBand === value ? "is-selected" : ""}
                      onClick={() => {
                        setQueueBand(value);
                        setError(undefined);
                      }}
                      aria-pressed={queueBand === value}
                    >
                      <Clock3 aria-hidden="true" />
                      {label}
                    </button>
                  ),
                )}
              </fieldset>
            )}

            {enablePhotoUpload && (
              <details className="report-photo-disclosure">
                <summary>
                  <span>
                    <ImagePlus aria-hidden="true" />
                    <span>
                      <b>写真を追加（任意）</b>
                      <small>場所や外観が分かる写真を1枚</small>
                    </span>
                  </span>
                  <span className="report-photo-summary-meta">
                    <em>5MBまで</em>
                    <ChevronDown aria-hidden="true" />
                  </span>
                </summary>
                <section className="report-photo-field" aria-label="投稿写真">

              {photo && photoPreviewUrl ? (
                <div className="report-photo-selected">
                  <img
                    src={photoPreviewUrl}
                    alt={`投稿予定の${photoKindLabel[photo.kind]}写真`}
                  />
                  <div>
                    <b>投稿予定の写真</b>
                    <small>{photo.file.name}</small>
                    <div className="report-photo-actions">
                      <label className="secondary-button report-photo-picker">
                        <ImagePlus aria-hidden="true" />
                        写真を変更
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={REPORT_PHOTO_ACCEPT}
                          aria-label="トイレの場所・外観の写真を選択"
                          onChange={(event) => {
                            const file = event.currentTarget.files?.[0];
                            if (file) replacePhoto(file);
                            event.currentTarget.value = "";
                          }}
                        />
                      </label>
                      <button
                        className="secondary-button report-photo-remove"
                        type="button"
                        onClick={removePhoto}
                      >
                        <Trash2 aria-hidden="true" />
                        写真を削除
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <label className="report-photo-empty">
                  <ImagePlus aria-hidden="true" />
                  <span>
                    <b>写真を選ぶ</b>
                    <small>JPEG・PNG・WebP</small>
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={REPORT_PHOTO_ACCEPT}
                    aria-label="トイレの場所・外観の写真を選択"
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      if (file) replacePhoto(file);
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
              )}

              {photo && (
                <fieldset className="photo-kind-options">
                  <legend>写真の内容</legend>
                  {(Object.entries(photoKindLabel) as [ToiletPhotoKind, string][]).map(
                    ([kind, label]) => (
                      <button
                        key={kind}
                        type="button"
                        className={photo.kind === kind ? "is-selected" : ""}
                        aria-pressed={photo.kind === kind}
                        onClick={() => setPhoto((current) => current && { ...current, kind })}
                      >
                        {label}
                      </button>
                    ),
                  )}
                </fieldset>
              )}

              {photoError && (
                <p className="form-error report-photo-error" role="alert">
                  {photoError}
                </p>
              )}
              <p className="report-photo-safety">
                人の顔、車のナンバー、私有地の内部が写らない写真を選んでください。公開前に掲載確認を行います。
              </p>
                </section>
              </details>
            )}

            <p className={`privacy-note${simulated ? " is-simulation" : ""}`}>
              <LockKeyhole aria-hidden="true" />
              <span>
                {simulated ? (
                  <>
                    <b>これは実証入力です。外部へ送信しません。</b>
                    入力内容と写真は、この端末の現在の表示だけに一時反映します。
                  </>
                ) : (
                  <>
                    <b>氏名・メール・正確な現在地は送信しません。</b>
                    施設ID、状況、待ち時間帯（該当時）、報告時刻、不正利用防止用のランダムな端末IDを送信します。
                    {enablePhotoUpload && "写真を選んだ場合は画像も送信し、位置情報などのメタデータを削除して掲載確認します。"}
                    端末IDはサーバーでハッシュ化します。
                  </>
                )}
                <a href="/site-information.html#privacy">個人情報保護の説明</a>
              </span>
            </p>

            <button
              className="primary-button report-submit"
              type="button"
              disabled={!status || (status === "queue" && !queueBand) || submitting}
              onClick={() => setReviewing(true)}
            >
              <Check aria-hidden="true" />
              入力内容を確認
            </button>
          </>
        )}
      </section>
    </div>
  );
}
